import React, { useEffect, useMemo, useState } from "react";
import { observer, inject } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import LoadingContainer from "../layouts/LoadingContainer";
import { subMonths, getMonth, getYear, format, isValid, parse } from "date-fns";
import { useMobile } from "../utils/mobile";
import MonthReports from "./MonthReports";
import MonthReportsMobile from "./MonthReportsMobile";
import ConfirmModal from "../components/ConfirmModal";

const MONTH_PARAM_FORMAT = "yyyy-MM";
const EXPENSES_TAB = "2";

// Month from ?month=yyyy-MM, defaulting to last month
const getMonthKey = (searchParams) => {
  const monthParam = searchParams.get("month");
  const parsed = monthParam ? parse(monthParam, MONTH_PARAM_FORMAT, new Date()) : null;
  return isValid(parsed)
    ? format(parsed, MONTH_PARAM_FORMAT)
    : format(subMonths(new Date(), 1), MONTH_PARAM_FORMAT);
};

const MonthReportsMain = ({ userStore, monthReportsStore }) => {
  const { t } = useTranslation("month_reports");
  const isMobile = useMobile();
  const [isLoading, setLoading] = useState(true);
  const [isListLoading, setListLoading] = useState(false);
  const [hasLoadedReports, setHasLoadedReports] = useState(false);
  const [gls, setGls] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [monthReports, setMonthReports] = useState([]);
  const [error, setError] = useState("");
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [period, setPeriod] = useState(null);
  const [gl, setGL] = useState(null);

  // Selected month and tab live in the URL, so they survive navigating to /update_report or
  // /expense and back
  const [searchParams, setSearchParams] = useSearchParams();
  const monthKey = getMonthKey(searchParams);
  const date = useMemo(() => parse(monthKey, MONTH_PARAM_FORMAT, new Date()), [monthKey]);
  const tab = searchParams.get("tab") === EXPENSES_TAB ? EXPENSES_TAB : "1";

  const updateSearchParams = (changes) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        return next;
      },
      { replace: true }
    );
  };

  const setTab = (newTab) => {
    updateSearchParams({ tab: newTab });
  };

  const keys = [
    { key: "CURDATE", width: "15%" },
    { key: "DAYNAME", width: "5%" },
    { key: "STANDARD", width: "5%" },
    { key: "ILL", width: "5%" },
    { key: "SERV", width: "5%" },
    { key: "VAC", width: "5%" },
    { key: "HOURS", width: "10%" },
    { key: "EXTRA", width: "5%" },
    { key: "HOLIDAYDES", width: "10%" },
    { key: "DES", width: "10%" },
    { key: "EXTFILEDES", width: "15%", link: true },
    { key: "ACTIONS", width: "10%" },
  ];

  const expKeys = [
    { key: "EXTFILEDES", width: "10%" },
    { key: "FILENAMEDES", width: "70%", link: true },
    { key: "VATPRICE", width: "10%" },
    { key: "ACTIONS", width: "10%" },
  ];

  const monthReportsKeys = [
    { key: "MONTHLYHOURS", width: "10%" },
    { key: "REPORTEDHOURS", width: "15%" },
    { key: "ILLHOURS", width: "10%" },
    { key: "VACHOURS", width: "10%" },
    { key: "SERVHOURS", width: "10%" },
    { key: "EXTRAHOURS", width: "10%" },
    { key: "MISSHOURS", width: "10%" },
    { key: "TOTAL", width: "10%" },
    { key: "ACTIONS", width: "10%" },
  ];

  const modifyMonthReportData = (monthReport) => {
    const totalHours = !!monthReport
      ? (
          (monthReport?.REPORTEDHOURS ?? 0) +
          (monthReport?.ILLHOURS ?? 0) +
          (monthReport?.VACHOURS ?? 0) +
          (monthReport?.SERVHOURS ?? 0) +
          Math.abs(monthReport?.EXTRA ?? 0)
        ).toFixed(2)
      : "0";

    const displayTotalHours = totalHours === "0.00" ? "0" : totalHours;
    const modifiedMonthReport = {
      ...monthReport,
      EXTRAHOURS: monthReport.EXTRA > 0 ? monthReport.EXTRA : 0,
      MISSHOURS: monthReport.EXTRA < 0 ? Math.abs(monthReport.EXTRA) : 0,
      TOTAL: displayTotalHours,
    };
    return modifiedMonthReport;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      monthReportsStore.setError("");
      try {
        await monthReportsStore.getMonthReportsStatuses();
        const gls = await monthReportsStore.getGls();

        if (!monthReportsStore.isError) {
          setGls(gls);
          setStatuses(monthReportsStore.monthReportsStatuses);
        }
      } catch (error) {
        console.error("Failed to fetch month report statuses / GLs", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const monthReport = modifyMonthReportData(monthReportsStore.monthReports);
    setMonthReports(monthReport);
  }, [monthReportsStore.monthReports]);

  useEffect(() => {
    const fetchMonthReports = async () => {
      setListLoading(true);
      monthReportsStore.setError("");

      const period = getMonth(date) + 1;
      const year = getYear(date);
      setPeriod(period);

      const gl = gls.find((gl) => gl.GLNAME === year.toString())?.GL;
      setGL(gl);
      try {
        if (!!gl) {
          const data = {
            ZCR_USERID: userStore.userDetails.ZCR_USERB,
            PERIOD: period,
            GL: gl,
          };

          await monthReportsStore.getMonthReports(data);

          const monthReport = modifyMonthReportData(monthReportsStore.monthReports);

          setMonthReports(monthReport);
        }
      } catch (error) {
        console.error("Failed to fetch month reports", { period, year }, error);
      } finally {
        setListLoading(false);
        if (gls?.length > 0) {
          setHasLoadedReports(true);
        }
      }
    };
    fetchMonthReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, gls]);

  const handelWorkHoursCalc = async () => {
    setListLoading(true);
    setError("");
    const body = {
      PERIOD: period,
      GL: gl,
      USERID: userStore.userDetails.ZCR_USERB,
    };
    const selectedMonthReport =
      monthReportsStore.monthReports.GL === gl &&
      monthReportsStore.monthReports.PERIOD === period;

    if (!selectedMonthReport) {
      await monthReportsStore.addReport({
        ...body,
        USERID: userStore.userDetails.ZCR_USERID,
      });
    }

    await monthReportsStore.updateReports({
      ...body,
      USERID: userStore.userDetails.ZCR_USERB,
      ZCR_USERID: userStore.userDetails.ZCR_USERB,
      data: { ZCOP_MONTHCALC: "Y" },
    });
    setListLoading(false);

    if (!!monthReportsStore.error) {
      setError(monthReportsStore.error);
    }
  };

  // Asks for confirmation first; the delete itself runs in confirmDeleteExpense
  const handelRowDeleteClick = (e, expense) => {
    e.preventDefault();
    e.stopPropagation();
    setExpenseToDelete(expense);
  };

  const confirmDeleteExpense = async () => {
    const expense = expenseToDelete;
    setExpenseToDelete(null);
    const data = {
      EXTFILENUM: expense.EXTFILENUM,
      ZCR_USERID: userStore.userDetails.ZCR_USERB,
      PERIOD: period,
      GL: gl,
    };
    setError("");
    setListLoading(true);
    try {
      await monthReportsStore.deleteExpense(data);

      if (!monthReportsStore.error) {
        const monthReport = modifyMonthReportData(monthReportsStore.monthReports);

        setMonthReports(monthReport);
      } else {
        setError(monthReportsStore.error);
      }
    } catch (error) {
      console.error("Failed to delete expense", data, error);
      setError("error:error_failed_delete_expense_req");
    } finally {
      setListLoading(false);
    }
  };

  const handleDateChange = (newDate) => {
    updateSearchParams({ month: format(newDate, MONTH_PARAM_FORMAT) });
  };

  const handleStatusChange = async (status) => {
    setError("");
    const data = {
      STATDES: status.label,
    };

    const body = {
      PERIOD: period,
      GL: gl,
      ZCR_USERID: userStore.userDetails.ZCR_USERB,
      data: data,
    };
    setListLoading(true);
    await monthReportsStore.updateReports(body);
    setListLoading(false);
    if (!monthReportsStore.error) {
      const monthReport = modifyMonthReportData(monthReportsStore.monthReports);

      setMonthReports(monthReport);
    } else {
      setError(monthReportsStore.error);
    }
  };

  const onSaveExpense = async (data) => {
    // Only an existing expense has EXTFILENUM (the same rule the mobile Expense page uses).
    // This used to check selectedExp here, which is never set, so every edit created a new expense
    data.EXTFILENUM !== undefined
      ? await monthReportsStore.updateExpense(data)
      : await monthReportsStore.addExpense(data);

    if (!monthReportsStore.error) {
      const monthReport = modifyMonthReportData(monthReportsStore.monthReports);

      setMonthReports(monthReport);
    }
  };

  const onSaveReport = async (data) => {
    await monthReportsStore.updateReport(data);

    if (!monthReportsStore.error) {
      const monthReport = modifyMonthReportData(monthReportsStore.monthReports);

      setMonthReports(monthReport);
    }
  };

  const viewProps = {
    keys,
    expKeys,
    monthReportsKeys,
    monthReports,
    handelWorkHoursCalc,
    handelRowDeleteClick,
    handleDateChange,
    handleStatusChange,
    onSaveExpense,
    onSaveReport,
    date,
    tab,
    setTab,
    isListLoading,
    statuses,
    gl,
    period,
    error,
  };

  return (
    // Full-page loader only on the first load; later refetches cover the list area only
    <LoadingContainer isLoading={isLoading || (gls?.length > 0 && !hasLoadedReports)}>
      {isMobile ? <MonthReportsMobile {...viewProps} /> : <MonthReports {...viewProps} />}
      {!!expenseToDelete && (
        <ConfirmModal
          title={t("delete_expense_title")}
          text={t("delete_expense_q")}
          handleAction={confirmDeleteExpense}
          onClose={() => setExpenseToDelete(null)}
        />
      )}
    </LoadingContainer>
  );
};

export default inject(
  "userStore",
  "monthReportsStore"
)(observer(MonthReportsMain));
